import { useState } from 'react';
import type { JSX } from 'react';

import { useLazyQuery } from '@apollo/client/react';
import { Form, Formik } from 'formik';
import * as yup from 'yup';

import { Button } from '@admin-platform/ui/components';
import { Heading } from '@admin-platform/ui/components';
import { useToast } from '@admin-platform/ui/components';

import SwitchArray from '../../components/FormikSwitchArray/SwitchArray';
import Container from '../../components/Layout/Container';
import normalizeGraphqlError from '../../utils/normalizeGraphqlError';

import { ROLE_OPTIONS, STATUS_OPTIONS } from './UserExport.constants';

import USER_EXPORT_QUERY from './user-exports.query.graphql';

import styles from './UserExport.module.css';

const SCHEMA = yup.object({
  roles: yup.array().of(yup.string()).required(),
  statuses: yup.array().of(yup.string()).required(),
});

export default function UserExport(): JSX.Element {
  const [getUsers] = useLazyQuery(USER_EXPORT_QUERY);
  const [isExported, setIsExported] = useState(false);

  const toast = useToast();
  return (
    <Container>
      <Heading level={1}>Export Users</Heading>

      <Formik
        initialValues={{
          roles: ['Admin', 'AdminCoach', 'SubAdmin', 'Coach'],
          statuses: ['Active'],
        }}
        validateOnChange
        validationSchema={SCHEMA}
        onSubmit={async (values, { setSubmitting }) => {
          const { roles, statuses } = SCHEMA.cast(values);

          try {
            const response = await getUsers({
              variables: {
                userRoles: roles,
                userStatuses: statuses,
              },
            });
            if (response.error) {
              toast(normalizeGraphqlError(response.error), 'error');
            } else {
              setIsExported(true);
              toast(
                'Request successfully sent. Your export will be sent to your email when is ready!',
                'success',
              );
            }
          } catch (err) {
            toast(normalizeGraphqlError(err), 'error');
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, isValid, values }) => (
          <Form className={styles.Form}>
            <div className={styles.FieldGroup}>
              <SwitchArray
                label="Select User Roles"
                name="roles"
                options={ROLE_OPTIONS}
                values={values.roles}
              />
            </div>
            <div className={styles.FieldGroup}>
              <SwitchArray
                label="Choose User Statuses"
                name="statuses"
                options={STATUS_OPTIONS}
                values={values.statuses}
              />
            </div>
            <Button
              type="submit"
              kind="blue"
              rounded
              disabled={isSubmitting || !isValid || isExported}
            >
              Export Users
            </Button>
          </Form>
        )}
      </Formik>
    </Container>
  );
}
